import { useCallback, useEffect, useRef, useState } from 'react';
import {
  deleteRecommendedDeck,
  loadRecommendedDecks,
  saveRecommendedDeck,
} from '../api/recommendedDecksApi.js';

const EMPTY = { unionId: '', unionName: '', viewerRole: '', decks: [] };

function initialData(unionId, unionName, viewerRole) {
  return {
    ...EMPTY,
    unionId: String(unionId || '').trim(),
    unionName: String(unionName || ''),
    viewerRole: String(viewerRole || ''),
  };
}

export function useRecommendedDecks({ unionId = '', unionName = '', viewerRole = '' } = {}) {
  const normalizedUnionId = String(unionId || '').trim();
  const normalizedUnionName = String(unionName || '');
  const normalizedViewerRole = String(viewerRole || '');
  const [data, setData] = useState(() => initialData(normalizedUnionId, normalizedUnionName, normalizedViewerRole));
  const [loading, setLoading] = useState(Boolean(normalizedUnionId));
  const [working, setWorking] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [notice, setNotice] = useState({ type: '', message: '' });
  const runRef = useRef(0);

  const refresh = useCallback(async ({ silent = false } = {}) => {
    if (!normalizedUnionId) {
      setLoading(false);
      setErrorMessage('활성 유니온 가입 정보를 확인할 수 없습니다.');
      return null;
    }

    const runId = runRef.current + 1;
    runRef.current = runId;
    if (!silent) setLoading(true);
    try {
      const next = await loadRecommendedDecks({
        unionId: normalizedUnionId,
        unionName: normalizedUnionName,
        viewerRole: normalizedViewerRole,
      });
      if (runRef.current !== runId) return null;
      setData(next);
      setErrorMessage('');
      return next;
    } catch (error) {
      if (runRef.current === runId) {
        setErrorMessage(error instanceof Error ? error.message : '추천덱을 불러오지 못했습니다.');
      }
      return null;
    } finally {
      if (runRef.current === runId && !silent) setLoading(false);
    }
  }, [normalizedUnionId, normalizedUnionName, normalizedViewerRole]);

  useEffect(() => {
    runRef.current += 1;
    setData(initialData(normalizedUnionId, normalizedUnionName, normalizedViewerRole));
    setErrorMessage('');
    setNotice({ type: '', message: '' });

    if (!normalizedUnionId) {
      setLoading(false);
      return undefined;
    }

    refresh();
    const changed = () => refresh({ silent: true });
    window.addEventListener('dororaid:recommended-decks-changed', changed);
    return () => {
      runRef.current += 1;
      window.removeEventListener('dororaid:recommended-decks-changed', changed);
    };
  }, [normalizedUnionId, normalizedUnionName, normalizedViewerRole, refresh]);

  const save = useCallback(async (input, id = '') => {
    if (working || !data.unionId) return false;
    setWorking(id ? `save:${id}` : 'create');
    setNotice({ type: '', message: '' });
    try {
      await saveRecommendedDeck({ unionId: data.unionId, id, input });
      await refresh({ silent: true });
      window.dispatchEvent(new CustomEvent('dororaid:recommended-decks-changed'));
      setNotice({ type: 'success', message: id ? '추천덱을 수정했습니다.' : '추천덱을 등록했습니다.' });
      return true;
    } catch (error) {
      setNotice({ type: 'error', message: error instanceof Error ? error.message : '추천덱을 저장하지 못했습니다.' });
      return false;
    } finally {
      setWorking('');
    }
  }, [data.unionId, refresh, working]);

  const remove = useCallback(async (id) => {
    if (working || !data.unionId || !id) return false;
    setWorking(`delete:${id}`);
    setNotice({ type: '', message: '' });
    try {
      await deleteRecommendedDeck({ unionId: data.unionId, id });
      await refresh({ silent: true });
      window.dispatchEvent(new CustomEvent('dororaid:recommended-decks-changed'));
      setNotice({ type: 'success', message: '추천덱을 삭제했습니다.' });
      return true;
    } catch (error) {
      setNotice({ type: 'error', message: error instanceof Error ? error.message : '추천덱을 삭제하지 못했습니다.' });
      return false;
    } finally {
      setWorking('');
    }
  }, [data.unionId, refresh, working]);

  return { ...data, loading, working, errorMessage, notice, refresh, save, remove };
}
